import type React from 'react';
import { Alert, IconButton, Stack, Link, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetHoldingAlertsQuery, alertUtils } from 'src/shared/api/alert';
import { useAppDispatch } from 'src/store';
import { type AlertType } from 'src/shared/types/alert';

const getAlertColor = (type: AlertType) => {
  if (type === 'info') return { background: '#EAF2FF', color: 'primary' };
  if (type === 'warning') return { background: '#FDEDDE' };
  return {};
};

export const HoldingAlertsSnackbar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: alerts, isLoading } = useGetHoldingAlertsQuery();

  const handleClose = (id: string) => {
    dispatch(
      alertUtils.updateQueryData('getHoldingAlerts', undefined, draft => {
        return draft.filter(alert => alert.id !== id);
      })
    );
  };

  if (isLoading || !alerts || alerts.length === 0) return null;

  return (
    <Stack sx={{ width: '100%', top: 0, zIndex: 1301 }}>
      {alerts.map(alert => (
        <Alert
          key={alert.id}
          severity={alert.type}
          sx={{
            ...getAlertColor(alert.type),
            alignItems: 'center',
            width: '100%',
            p: '21px 24px'
          }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1, ml: 1 }}>
              {alert.buttonText && alert.buttonLink && (
                <Link
                  color="inherit"
                  href={alert.buttonLink}
                  target="_blank"
                  sx={{ mr: 1, fontSize: 16, whiteSpace: 'nowrap' }}
                >
                  {alert.buttonText}
                </Link>
              )}
              {alert.displayRule === 'once' && (
                <IconButton
                  aria-label="close"
                  sx={{ color: '#4E5775' }}
                  onClick={() => {
                    handleClose(alert.id);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          }
        >
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>{alert.title}</div>
          <div style={{ fontSize: 15 }}>{alert.message}</div>
        </Alert>
      ))}
    </Stack>
  );
};
